<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="p-4 border-b border-gray-200 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">{{ t('admin.editor.properties') }}</h3>
        <p class="text-sm text-gray-600">{{ component.name || t('admin.editor.selectedComponent') }}</p>
      </div>
      <Button 
        icon="pi pi-times"
        text
        class="text-gray-400 hover:text-gray-600"
        @click="$emit('close')"
      />
    </div>
    
    <!-- Property Categories -->
    <div class="flex-1 overflow-y-auto">
      <Accordion multiple :value="['content']">
        <!-- Content Properties -->
        <AccordionPanel value="content">
          <AccordionHeader>
            <i class="pi pi-file-edit mr-2"></i>
            {{ t('admin.editor.content') }}
          </AccordionHeader>
          <AccordionContent>
            <div class="space-y-4 p-1">
              <template v-for="[propName, propDef] in getPropertiesForCategory('content')" :key="propName">
                <!-- String Input -->
                <div v-if="propDef.type === 'string'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>

                <!-- HTML content field, edited as PLAIN TEXT (D1): owner sees paragraphs,
                     not <p> markup. Strip on load, re-wrap to HTML on save. -->
                <div v-else-if="propDef.type === 'textarea' && propDef.html">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <Textarea
                    :model-value="htmlDrafts[propName] ?? ''"
                    class="w-full"
                    rows="6"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleHtmlInput(propName, ($event.target as HTMLTextAreaElement).value)"
                  />
                </div>

                <!-- Textarea Input (plain text) -->
                <div v-else-if="propDef.type === 'textarea'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <Textarea
                    :model-value="component[propName] || ''"
                    class="w-full"
                    rows="4"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLTextAreaElement).value)"
                  />
                </div>

                <!-- Select Input -->
                <div v-else-if="propDef.type === 'select'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <Select
                    :model-value="component[propName] || (propDef.options ? propDef.options[0] : '')"
                    :options="propDef.options?.map(opt => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))"
                    option-label="label"
                    option-value="value"
                    class="w-full"
                    @change="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Number Input -->
                <div v-else-if="propDef.type === 'number'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputNumber
                    :model-value="component[propName] || 0"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Boolean Input -->
                <div v-else-if="propDef.type === 'boolean'">
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      :model-value="component[propName] || false"
                      binary
                      @change="handleUpdate(propName, $event)"
                    />
                    <label class="text-sm font-medium text-gray-700">{{ propDef.label }}</label>
                  </div>
                </div>

                <!-- URL Input -->
                <div v-else-if="propDef.type === 'url'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    type="url"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>

                <!-- Image Field -->
                <div v-else-if="propDef.type === 'image'">
                  <ImageField
                    :model-value="component[propName] || null"
                    :label="propDef.label"
                    :project-id="projectId"
                    @update:modelValue="handleUpdate(propName, $event)"
                  />
                </div>

                <!-- Array Field -->
                <div v-else-if="propDef.type === 'array'">
                  <ArrayField
                    :model-value="component[propName] || []"
                    :label="propDef.label"
                    :item-schema="getArrayItemSchemaForField(propName)"
                    :project-id="projectId"
                    @update:modelValue="handleUpdate(propName, $event)"
                  />
                </div>
              </template>
            </div>
          </AccordionContent>
        </AccordionPanel>
        
        <!-- Appearance Properties -->
        <AccordionPanel value="appearance">
          <AccordionHeader>
            <i class="pi pi-palette mr-2"></i>
            {{ t('admin.editor.appearance') }}
          </AccordionHeader>
          <AccordionContent>
            <div class="space-y-4 p-1">
              <template v-for="[propName, propDef] in getPropertiesForCategory('appearance')" :key="propName">
                <!-- Select Input -->
                <div v-if="propDef.type === 'select'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <Select
                    :model-value="component[propName] || (propDef.options ? propDef.options[0] : '')"
                    :options="propDef.options?.map(opt => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))"
                    option-label="label"
                    option-value="value"
                    class="w-full"
                    @change="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Color Input -->
                <div v-else-if="propDef.type === 'color'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <div class="flex items-center space-x-2">
                    <ColorPicker
                      :model-value="stripHash(component[propName] || '#ffffff')"
                      @update:modelValue="handleUpdate(propName, '#' + $event)"
                    />
                    <InputText
                      :model-value="component[propName] || '#ffffff'"
                      class="flex-1"
                      @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                    />
                  </div>
                </div>

                <!-- Image Input (in appearance category, e.g. background image) -->
                <div v-else-if="propDef.type === 'image'">
                  <ImageField
                    :model-value="component[propName] || null"
                    :label="propDef.label"
                    :project-id="projectId"
                    @update:modelValue="handleUpdate(propName, $event)"
                  />
                </div>

                <!-- Number Input -->
                <div v-else-if="propDef.type === 'number'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputNumber
                    :model-value="component[propName] || 0"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Boolean Input -->
                <div v-else-if="propDef.type === 'boolean'">
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      :model-value="component[propName] || false"
                      binary
                      @change="handleUpdate(propName, $event)"
                    />
                    <label class="text-sm font-medium text-gray-700">{{ propDef.label }}</label>
                  </div>
                </div>

                <!-- URL Input -->
                <div v-else-if="propDef.type === 'url'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    type="url"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>

                <!-- String Input -->
                <div v-else-if="propDef.type === 'string'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>
              </template>
            </div>
          </AccordionContent>
        </AccordionPanel>
        
        <!-- Layout Properties -->
        <AccordionPanel value="layout">
          <AccordionHeader>
            <i class="pi pi-th-large mr-2"></i>
            {{ t('admin.editor.layout') }}
          </AccordionHeader>
          <AccordionContent>
            <div class="space-y-4 p-1">
              <template v-for="[propName, propDef] in getPropertiesForCategory('layout')" :key="propName">
                <!-- Select Input -->
                <div v-if="propDef.type === 'select'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <Select
                    :model-value="component[propName] || (propDef.options ? propDef.options[0] : '')"
                    :options="propDef.options?.map(opt => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))"
                    option-label="label"
                    option-value="value"
                    class="w-full"
                    @change="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Number Input -->
                <div v-else-if="propDef.type === 'number'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputNumber
                    :model-value="component[propName] || 0"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, $event.value)"
                  />
                </div>

                <!-- Boolean Input -->
                <div v-else-if="propDef.type === 'boolean'">
                  <div class="flex items-center space-x-2">
                    <Checkbox
                      :model-value="component[propName] || false"
                      binary
                      @change="handleUpdate(propName, $event)"
                    />
                    <label class="text-sm font-medium text-gray-700">{{ propDef.label }}</label>
                  </div>
                </div>

                <!-- URL Input -->
                <div v-else-if="propDef.type === 'url'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    type="url"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>

                <!-- String Input -->
                <div v-else-if="propDef.type === 'string'">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ propDef.label }}</label>
                  <InputText
                    :model-value="component[propName] || ''"
                    class="w-full"
                    :placeholder="t('admin.editor.enterField', { field: propDef.label.toLowerCase() })"
                    @input="handleUpdate(propName, ($event.target as HTMLInputElement).value)"
                  />
                </div>
              </template>
            </div>
          </AccordionContent>
        </AccordionPanel>
        
        <!-- Advanced Properties -->
        <AccordionPanel value="advanced">
          <AccordionHeader>
            <i class="pi pi-cog mr-2"></i>
            {{ t('admin.editor.advanced') }}
          </AccordionHeader>
          <AccordionContent>
            <div class="space-y-4 p-1">
              <!-- CSS Classes -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('admin.editor.cssClasses') }}</label>
                <InputText 
                  :model-value="component.cssClasses || ''"
                  class="w-full"
                  placeholder="custom-class another-class"
                  @input="handleUpdate('cssClasses', ($event.target as HTMLInputElement).value)"
                />
              </div>
              
              <!-- Component ID -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('admin.editor.componentId') }}</label>
                <InputText 
                  :model-value="component.id || ''"
                  class="w-full"
                  placeholder="unique-id"
                  @input="handleUpdate('id', ($event.target as HTMLInputElement).value)"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
    
    <!-- Actions -->
    <div class="p-4 border-t border-gray-200 space-y-2">
      <Button
        :label="t('admin.duplicate')"
        icon="pi pi-copy"
        outlined
        class="w-full"
        @click="$emit('duplicate')"
      />
      <Button
        :label="t('admin.delete')"
        icon="pi pi-trash"
        severity="danger"
        outlined
        class="w-full"
        @click="$emit('delete')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import ColorPicker from 'primevue/colorpicker'
import Checkbox from 'primevue/checkbox'
import InputNumber from 'primevue/inputnumber'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import ArrayField from '@/components/admin/fields/ArrayField.vue'
import ImageField from '@/components/admin/fields/ImageField.vue'
import { getComponentSchema, getPropertiesByCategory, getArrayItemSchema, type PropertyDefinition } from '@/schemas/componentSchemas'
import { useI18n } from 'vue-i18n'
import { watch } from 'vue'
import { useHtmlDraft } from '@/composables/useHtmlDraft'

interface Props {
  component: any
  projectId: number
}

const props = defineProps<Props>()
const { t } = useI18n()

// D1 plain-text editing for html-flagged content fields (shared composable). Re-seeded
// when the SELECTED component changes — NOT on every keystroke, so re-wrapping to HTML
// on input doesn't re-derive the draft and jump the cursor.
const { htmlDrafts, seed, write } = useHtmlDraft()
watch(
  () => props.component?.id ?? props.component?.__component,
  () => {
    const type = props.component?.__component || props.component?.type
    if (!type) return
    seed(
      [...getPropertiesByCategory(type, 'content')]
        .filter(([, def]) => def.html)
        .map(([name]) => [name, props.component[name] ?? '']),
    )
  },
  { immediate: true },
)

// Store stays HTML so the renderer is unchanged; owner only ever sees plain text.
function handleHtmlInput(propName: string, plain: string) {
  write(propName, plain, (html) => handleUpdate(propName, html))
}


// Helper functions
const getSchemaForComponent = () => {
  const componentType = props.component.__component || props.component.type
  return getComponentSchema(componentType)
}

const getPropertiesForCategory = (category: string) => {
  const componentType = props.component.__component || props.component.type
  return getPropertiesByCategory(componentType, category)
}

const hasProperty = (property: string): boolean => {
  const schema = getSchemaForComponent()
  return property in schema.properties
}

const getArrayItemSchemaForField = (fieldName: string) => {
  const componentType = props.component.__component || props.component.type
  return getArrayItemSchema(componentType, fieldName)
}

// Strip '#' prefix for PrimeVue ColorPicker (expects hex without #)
const stripHash = (color: string): string => color.replace(/^#/, '')

// Event handlers
const emit = defineEmits<{
  update: [property: string, value: any]
  close: []
  duplicate: []
  delete: []
}>()

const handleUpdate = (property: string, value: any) => {
  emit('update', property, value)
}
</script>